import { useAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import EmbedController from "../../../../embed/EmbedController";
// Slack alerts are off until a new webhook is implemented
// import AlertSlackOfError from "../../../../monitoring/AlertSlackOfError";
import { useComponentsIndex } from "../../../hooks/useComponentsIndex";
import { useMgpCartKeys } from "../../../hooks/useMgpCartKeys";
import { usePrevious } from "../../../hooks/usePrevious";
import { createUniqueShopifyProduct } from "../../../modules/createUniqueShopifyProduct";
import { delay } from "../../../modules/delay";
import { getFinalPrice } from "../../../modules/getFinalPrice";
import { getShoppingCartData } from "../../../modules/getShoppingCartData";
import { configurationPageUrl, hostedConfigurationUrls } from "../../../modules/configurationPageUrl";
import { isHostedHttpUrl } from "../../../modules/isHostedHttpUrl";
import { cartHasLicensedEngraving } from "../../../modules/mgpBasePrice";
import { mgpCartToLineItemProperties } from "../../../modules/shopifyLineItem";
import { hostCustomLogo } from "../../../modules/uploadImageToDigitalOcean";
import { canvas_base64, session_id } from "../../dataManagers/GlobalDataManagers";
import { Boop } from "../Animations/Boop";
import { BottomNavigation } from "../BuildSection/BottomNavigation";
import { CardElementCart } from "../CardElement/CardElementCart";
import "./styles.scss";

export const ShoppingCart = ({ productsState, componentsStateObj, itemsStateObj, previousComponentsState }) => {
  const [show, setShow] = useState(false);
  const [showInvalidCartModal, setShowInvalidCartModal] = useState(false);
  const [isCartSubmissionPending, setIsCartSubmissionPending] = useState(false);
  const [isCartReadyForSubmission, setIsCartReadyForSubmission] = useState(false);
  const { handleSubmit, register, watch } = useForm();
  const componentsIndexObj = useComponentsIndex(componentsStateObj.state);
  const { baseColorDefaultIndex, engravingsIndex } = useComponentsIndex(componentsStateObj.state);
  const ttCartData = useMemo(
    () => getShoppingCartData(productsState, componentsStateObj.state, itemsStateObj.state, componentsIndexObj),
    [itemsStateObj.state],
  );
  const cartSubmissionObj = useMgpCartKeys(productsState, componentsStateObj.state, itemsStateObj.state, ttCartData);
  const discountedPrice = getFinalPrice(productsState, ttCartData);
  const msrpPrice = getFinalPrice(productsState, ttCartData, true);
  const [sessionId] = useAtom(session_id);
  const previousSessionId = usePrevious(sessionId);
  const hostedPropertyOverrides = useRef({});

  const handleCloseModal = () => setShow(false);
  const handleShowModal = () => setShow(true);
  const handleBackClick = () =>
    componentsStateObj.setActiveId(
      previousComponentsState.includes("cart") ? componentsStateObj.state.array[baseColorDefaultIndex]._id : previousComponentsState,
    );

  // setup callback for response when parent cart is updated
  useEffect(() => {
    if (EmbedController.isEmbedded) {
      EmbedController.setCartResponseCallback((errorMsg, redirectUrl) => {
        if (errorMsg || !redirectUrl) {
          console.log(`ERROR adding to shopping cart`);
          setIsCartSubmissionPending(false);
          setIsCartReadyForSubmission(false);
          setModalHeader("");
          setTimeout(() => setModalHeader("ERROR: please try again"), 500);
          // Slack alerts are off until a new webhook is implemented
          // AlertSlackOfError("ShoppingCart.jsx cart response", `error: ${errorMsg}`, sessionId);
          alert("Error submitting to cart. Please try again.");
        } else if (redirectUrl) {
          console.log(`cart redirectUrl in shopping cart: ${redirectUrl}`);
          handleCloseModal();
          setIsCartSubmissionPending(false);
          setIsCartReadyForSubmission(false);
          sessionId !== previousSessionId && sendTransactionAnalytics();
          EmbedController.sendParentUrlChange(redirectUrl);
        }
      });
    }
  }, []);

  /**
   *
   * send product data to MGP parent site's cart and redirect shopper there
   *
   */

  // runs when checkout btn is clicked
  const handleCheckout = async () => {
    setIsCartSubmissionPending(true);

    try {
      if (EmbedController.isEmbedded) {
        await uploadCartLogos();
      }
      setIsCartReadyForSubmission(true);
    } catch (error) {
      console.log("ERROR uploading custom logo", error);
      setIsCartSubmissionPending(false);
      setIsCartReadyForSubmission(false);
      setModalHeader("");
      setTimeout(() => setModalHeader("ERROR: please try again"), 500);
      // Slack alerts are off until a new webhook is implemented
      // AlertSlackOfError("ShoppingCart.jsx logo upload", `error: ${error}`, sessionId);
      alert(`Custom logo upload failed. ${error && error.message ? error.message : error}`);
    }
  };

  async function uploadCartLogos() {
    const newItemIds = { ...itemsStateObj.state.activeIds };
    const overrides = {};
    let didHost = false;

    const litTextureSrc = getLitTextureData();
    if (litTextureSrc) {
      const savedLitTextureUrl = await hostedLogoUrl(litTextureSrc);
      if (!isHostedHttpUrl(litTextureSrc)) {
        if (!newItemIds.baseColor_default.inputs) newItemIds.baseColor_default.inputs = {};
        newItemIds.baseColor_default.inputs.uploaded_logo_src = savedLitTextureUrl;
        didHost = true;
      }
      overrides["options[lit-texture-src]"] = savedLitTextureUrl;
    }

    const [logoObjsInCart, platesWithLogoSrc] = getCustomLogoData();
    if (logoObjsInCart && platesWithLogoSrc) {
      let savedLogoUrl_front = platesWithLogoSrc.front ? await hostedLogoUrl(platesWithLogoSrc.front) : null;
      let savedLogoUrl_rear = platesWithLogoSrc.rear ? await hostedLogoUrl(platesWithLogoSrc.rear) : null;
      if (savedLogoUrl_rear === "frontCopy") savedLogoUrl_rear = savedLogoUrl_front;

      const engravingsComponentName = componentsStateObj.state.array[engravingsIndex]._id;
      const writeLogoInput = (position, savedUrl) => {
        const engraving = newItemIds[engravingsComponentName]?.[position]?.engraving;
        if (!engraving || !savedUrl || savedUrl === "frontCopy") return;
        if (!engraving.inputs) engraving.inputs = {};
        engraving.inputs.uploaded_logo_src = savedUrl;
      };
      if (savedLogoUrl_front) {
        writeLogoInput("front", savedLogoUrl_front);
        overrides["options[design-front-image-custom]"] = savedLogoUrl_front;
        if (!isHostedHttpUrl(platesWithLogoSrc.front)) didHost = true;
      }
      if (savedLogoUrl_rear) {
        writeLogoInput("rear", savedLogoUrl_rear);
        overrides["options[design-rear-image-custom]"] = savedLogoUrl_rear;
        if (platesWithLogoSrc.rear !== "frontCopy" && !isHostedHttpUrl(platesWithLogoSrc.rear)) didHost = true;
      }
    }

    hostedPropertyOverrides.current = overrides;
    Object.assign(cartSubmissionObj, overrides);

    if (didHost) {
      itemsStateObj.setActiveId(newItemIds);
      // Let the hash pick up the hosted logo before the configuration URL is saved.
      await delay(1000);
    }
  }

  function configurationUrlForCart(cartObj) {
    return configurationPageUrl({
      parentUrl: EmbedController.parentUrl,
      href: window.location.href,
      hash: window.location.hash,
      hostedUrls: hostedConfigurationUrls(cartObj),
    });
  }

  async function hostedLogoUrl(src) {
    if (!src || src === "frontCopy") return src;
    if (isHostedHttpUrl(src)) return src;
    if (String(src).indexOf("data:") === 0) return hostCustomLogo(src);
    return src;
  }

  const [custom_image_base64, setCustomImageBase64] = useAtom(canvas_base64);

  // we only submit to the cart once product screenshot has been taken and the cart is ready for submission
  useEffect(() => {
    if (custom_image_base64 && isCartReadyForSubmission) {
      submitToShoppingCart();
    }
  }, [custom_image_base64, isCartReadyForSubmission]);

  function submitToShoppingCart() {
    if (!EmbedController.isEmbedded) {
      setIsCartSubmissionPending(false);
      setIsCartReadyForSubmission(false);
      alert("Shopify cart is unavailable. Open the configurator from the shop page so the parent can POST /cart/add.js.");
      return;
    }

    submitShopifyCart();
  }

  async function submitShopifyCart() {
    try {
      Object.assign(cartSubmissionObj, hostedPropertyOverrides.current);
      // uploadCartLogos already waited for the hash when a logo was hosted.
      const properties = mgpCartToLineItemProperties(cartSubmissionObj, {
        licensed: cartHasLicensedEngraving(ttCartData),
        customURL: configurationUrlForCart(cartSubmissionObj),
      });

      const created = await createUniqueShopifyProduct({
        productObj: productsState.activeObj,
        properties,
        finalPrice: msrpPrice,
        customImageBase64: custom_image_base64,
        cartData: ttCartData,
      });
      if (created.imageUrl) cartSubmissionObj.custom_image = created.imageUrl;
      console.log("cartSubmissionObj :>> ", cartSubmissionObj);

      EmbedController.sendAddToCart("/cart/add.js", {
        items: [
          {
            id: created.variantId,
            quantity: 1,
            properties,
          },
        ],
      });
    } catch (error) {
      console.log("ERROR submitShopifyCart", error);
      setIsCartSubmissionPending(false);
      setIsCartReadyForSubmission(false);
      setModalHeader("");
      setTimeout(() => setModalHeader("ERROR: please try again"), 500);
      // Slack alerts are off until a new webhook is implemented
      // AlertSlackOfError("ShoppingCart.jsx submitShopifyCart", `error: ${error}`, sessionId);
      alert(`Shopify cart is unavailable. ${error && error.message ? error.message : error}`);
    }
  }

  function sendTransactionAnalytics() {
    EmbedController.sendGTMAnalyticsEvent({
      event: "purchase",
      ecommerce: {
        purchase: {
          actionField: {
            id: sessionId, // Required - random unique id
            affiliation: "Online Store",
            revenue: discountedPrice,
            tax: 0,
            shipping: 0,
          },
          products: [
            {
              name: productsState.activeObj.displayName, // Name or ID is required.
              id: `${productsState.activeObj.part_number}CUSDF`,
              price: discountedPrice,
              quantity: 1,
            },
          ],
        },
      },
    });
  }

  function getLitTextureData() {
    let litTextureObj = ttCartData.find((cartObj) => cartObj._id == "lit_baseColor");
    if (isHostedHttpUrl(litTextureObj?.uploadedLogoSrc)) return litTextureObj.uploadedLogoSrc;
    return litTextureObj?.uploadedLogoBase64;
  }

  function getCustomLogoData() {
    // build array of custom_logo's in the cart (none, front, rear, or both)
    let logoObjsInCart = ttCartData.filter((cartObj) => {
      if (cartObj._id?.includes("custom_logo")) return cartObj;
    });

    // only continue if there are custom logos in cart
    if (logoObjsInCart.length == 0) return [null];

    // will inform us which plates have a custom logo on it
    let platesWithLogoMap = { front: null, rear: null };

    // filter out duplicate logos
    logoObjsInCart.forEach((logoCartObj) => {
      if (logoCartObj._id.includes("front")) {
        platesWithLogoMap.front = logoCartObj.previewImg;
      } else if (logoCartObj._id.includes("rear")) {
        platesWithLogoMap.rear = logoCartObj.previewImg;
      }
    });

    // if rear logo is same as the front we'll edit the field saying they are duplicates
    if (platesWithLogoMap.front === platesWithLogoMap.rear) platesWithLogoMap.rear = "frontCopy";

    return [logoObjsInCart, platesWithLogoMap];
  }

  const [modalHeader, setModalHeader] = useState("TERMS AND CONDITIONS");
  useEffect(() => {
    if (isCartSubmissionPending) setModalHeader("Enjoy Your Purchase!");
  }, [isCartSubmissionPending]);

  const acceptTermsModal = () => (
    <Modal
      className="terms-and-conditions-modal"
      show={show}
      size="md"
      style={{ zIndex: 1202 }}
      backdrop="static"
      onHide={() => handleCloseModal()}
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title className="mx-auto text-uppercase">{modalHeader}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Custom orders are made-to-order. Due to the nature of the product, we cannot accept cancellations, exchanges or returns. We strongly recommend you
          confirm fitment prior to placing your order.
        </p>
        <form onSubmit={handleSubmit(handleCheckout)}>
          <label className="fw-bold">
            <input className="mx-1" name="acceptTerms" type="checkbox" {...register("acceptTerms", { required: true })} />I understand that my order cannot be
            canceled, exchanged, or returned
          </label>
          <br />
          <div className="d-flex justify-content-center mt-2">
            <button className="mx-2 px-2 submitBtn" disabled={!watch("acceptTerms") || isCartSubmissionPending} type="submit">
              I Agree
              {isCartSubmissionPending && <Spinner animation="border" style={{ width: "inherit", height: "inherit", verticalAlign: "0", marginLeft: "5px" }} />}
            </button>
            <button type="button" className="mx-2 px-2" style={{ backgroundColor: "grey" }} onClick={() => handleCloseModal()}>
              Go Back
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );

  const invalidCartModal = () => (
    <Modal show={showInvalidCartModal} size="md" style={{ zIndex: 1202 }} onHide={() => setShowInvalidCartModal(false)} keyboard={false}>
      <Modal.Header>
        <Modal.Title className="mx-auto text-uppercase">Invalid Cart</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          You selected a <strong>Custom Color</strong> but forgot to add the <strong>Paint Name</strong> or <strong>Paint Code</strong>.
        </p>
        <p>Please go back to the color step and include both fields or select a new color!</p>
        <div className="d-flex justify-content-center mt-2">
          <button
            className="mx-2 px-2"
            style={{ backgroundColor: "grey" }}
            onClick={() => componentsStateObj.setActiveId(componentsStateObj.state.array[baseColorDefaultIndex]._id)}
          >
            Take me
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );

  const handleAddToCartClick = () => {
    // check to confirm both paintName and paintCode were added before checkout
    if (
      itemsStateObj.state.activeIds.baseColor_default._id === "custom_baseColor" &&
      (!itemsStateObj.state.activeIds.baseColor_default?.inputs?.paintName || !itemsStateObj.state.activeIds.baseColor_default?.inputs?.paintCode)
    ) {
      setShowInvalidCartModal(true);
      return;
    }

    setIsCartReadyForSubmission(false);

    // signal to Scene.js to take a screenshot of caliper covers for the cart submission
    setCustomImageBase64(null);
    document.dispatchEvent(new CustomEvent("ScreenshotCanvasForCartImage"));

    handleShowModal();

    if (EmbedController.isEmbedded) {
      EmbedController.sendGTMAnalyticsEvent({
        event: "Add-to-Cart-BuildSection-MGP",
      });
    }
  };

  return (
    <>
      <div className="ShoppingCart my-3">
        <div className="d-flex align-items-center justify-content-around mb-1">
          <Boop boopType="scale" scale="1.1" timing={200}>
            <img
              className="backButton"
              onClick={handleBackClick}
              onTouchStart={(e) => {
                e.preventDefault();
                handleBackClick();
              }}
              src="/images/backButton.png"
              alt="back"
            />
          </Boop>
          <h4 className="mb-0 font-weight-bold">Purchase Summary</h4>
          <div style={{ width: "30px" }}></div>
        </div>
        <p className="text-center brand-color mb-3">{`*set of ${productsState.activeObj.set_count} caliper covers`}</p>
        <div className="d-flex mt-4 mb-3">
          <h5>{`Total: $${discountedPrice}`}</h5>
          <button
            onClick={handleAddToCartClick}
            onTouchStart={(e) => {
              e.preventDefault();
              handleAddToCartClick();
            }}
            className="ms-auto checkoutBtn"
          >
            Add To Cart
          </button>
        </div>

        <CardElementCart cardContentArray={ttCartData} cardsPerRow={2} imgFrame={false} />
        <BottomNavigation handleAddToCartClick={handleAddToCartClick} componentsStateObj={componentsStateObj} />
      </div>

      <div className="Modal">
        {acceptTermsModal()}
        {invalidCartModal()}
      </div>
    </>
  );
};
